/**
 * Affected-individual roster — per-recipient notification tracking (164.404).
 *
 * Import the vendor/forensic roster (CSV or Excel, e.g. the Kroll portal
 * export), then track every person through the notice lifecycle: pending →
 * mailed → delivered, or returned → substitute notice. The 164.404(d)(2)
 * substitute-notice threshold (10 or more unreachable) is computed server-side
 * and surfaced as a banner the moment the roster crosses it. All parsing is
 * deterministic server code, no AI.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PhiFriendlyNote from '../../components/PhiFriendlyNote.jsx';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_OPTIONS = [
  ['pending', 'Pending'], ['mailed', 'Mailed'], ['delivered', 'Delivered'],
  ['returned', 'Returned'], ['substitute_notice', 'Substitute notice'], ['excluded', 'Excluded'],
];
const STATUS_STYLES = {
  pending: 'bg-slate-100 text-slate-600',
  mailed: 'bg-blue-50 text-blue-700',
  delivered: 'bg-emerald-50 text-emerald-700',
  returned: 'bg-red-50 text-red-700',
  substitute_notice: 'bg-amber-50 text-amber-700',
  excluded: 'bg-slate-50 text-slate-400 line-through',
};

const PAGE_SIZE = 100;
const input = 'px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200';
const btn = 'px-3 py-1.5 rounded-lg text-sm font-medium';

async function api(path, { method = 'GET', body } = {}) {
  const res = await fetch(`${API}/api/privacy${path}`, {
    method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.message || 'Something went wrong.');
  return json;
}

function StatChip({ label, value, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <div className={`px-3 py-1.5 rounded-xl border text-xs font-medium ${tones[tone]}`}>
      <span className="text-base font-bold mr-1.5">{value}</span>{label}
    </div>
  );
}

export default function AffectedIndividuals({ incidentId, canEdit }) {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [importNote, setImportNote] = useState('');
  const [replace, setReplace] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
      if (statusFilter) qs.set('status', statusFilter);
      if (search.trim()) qs.set('search', search.trim());
      const [list, st] = await Promise.all([
        api(`/incidents/${incidentId}/individuals?${qs.toString()}`),
        api(`/incidents/${incidentId}/individuals/stats`),
      ]);
      setRows(list.individuals || []);
      setTotal(list.total || 0);
      setStats(st.stats || null);
      setError('');
    } catch (e) { setError(e.message); }
  }, [incidentId, page, statusFilter, search]);
  useEffect(() => { load(); }, [load]);

  const importFile = async (file) => {
    if (!file) return;
    setBusy(true); setImportNote(''); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (replace) fd.append('replace', '1');
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/individuals/import`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || 'Import failed.');
      setImportNote(
        `Imported ${json.imported} individual(s) from "${file.name}"` +
        (json.removed ? `, replaced ${json.removed} existing` : '') +
        (json.skipped ? `, skipped ${json.skipped} row(s) with no identifying data` : '') +
        (json.unmapped_columns?.length ? `. Ignored columns: ${json.unmapped_columns.join(', ')}.` : '.')
      );
      setPage(0);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const patchRow = async (id, patch) => {
    try {
      const r = await api(`/incidents/${incidentId}/individuals/${id}`, { method: 'PATCH', body: patch });
      setRows((rs) => rs.map((x) => (x.id === id ? r.individual : x)));
      const st = await api(`/incidents/${incidentId}/individuals/stats`);
      setStats(st.stats || null);
    } catch (e) { alert(e.message); }
  };

  const bulkMail = async () => {
    if (!window.confirm(`Mark all ${stats?.pending || 0} pending individuals as mailed today?`)) return;
    setBusy(true);
    try {
      const r = await api(`/incidents/${incidentId}/individuals/bulk-status`, {
        method: 'POST', body: { from_status: 'pending', to_status: 'mailed', method: 'first_class_mail' },
      });
      setImportNote(`Marked ${r.updated} individual(s) as mailed.`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this individual from the roster?')) return;
    try { await api(`/incidents/${incidentId}/individuals/${id}`, { method: 'DELETE' }); await load(); }
    catch (e) { alert(e.message); }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const sub = stats?.substitute;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-bold text-slate-900">Affected individuals</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            The notification roster: import the vendor or forensic member file, then track each person's notice. Substitute-notice math follows 45 CFR 164.404(d).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {total > 0 && (
            <a href={`${API}/api/privacy/incidents/${incidentId}/individuals/export.csv`}
              className={btn + ' border border-slate-200 text-slate-700 hover:bg-slate-50'}>
              Download CSV
            </a>
          )}
          {canEdit && (
            <>
              <label className="flex items-center gap-1.5 text-xs text-slate-500">
                <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
                Replace existing
              </label>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" disabled={busy}
                onChange={(e) => importFile(e.target.files?.[0])}
                className="text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-slate-800" />
            </>
          )}
        </div>
      </div>

      {canEdit && <PhiFriendlyNote variant="minimum-necessary" />}

      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {importNote && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{importNote}</div>}

      {sub?.required && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-bold">Substitute notice required ({sub.basis}).</span> {sub.note}
        </div>
      )}
      {sub && !sub.required && sub.unreachable > 0 && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          {sub.note} ({sub.basis})
        </div>
      )}

      {stats && stats.total > 0 && (
        <div className="flex flex-wrap gap-2">
          <StatChip label="total" value={stats.total} />
          <StatChip label="pending" value={stats.pending} />
          <StatChip label="mailed" value={stats.mailed} tone="blue" />
          <StatChip label="delivered" value={stats.delivered} tone="emerald" />
          <StatChip label="returned" value={stats.returned} tone="red" />
          <StatChip label="substitute" value={stats.substitute_notice} tone="amber" />
          <StatChip label="insufficient contact" value={stats.insufficient_contact} tone={stats.insufficient_contact > 0 ? 'amber' : 'slate'} />
        </div>
      )}

      {stats && stats.total === 0 && (
        <p className="text-sm text-slate-400 py-6 text-center">
          No individuals on the roster yet. Import the member file you received from the vendor or forensic team (CSV or Excel); columns like name, address, city, state, zip, email, and member ID are recognized automatically.
        </p>
      )}

      {total > 0 && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search name, member ID, email, city" className={input + ' w-72'} />
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} className={input}>
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {canEdit && (stats?.pending || 0) > 0 && (
              <button onClick={bulkMail} disabled={busy} className={btn + ' bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 ml-auto'}>
                Mark all pending as mailed
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3 font-medium">Name</th>
                  <th className="py-2 pr-3 font-medium">Member ID</th>
                  <th className="py-2 pr-3 font-medium">Contact</th>
                  <th className="py-2 pr-3 font-medium">Data exposed</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Dates</th>
                  {canEdit && <th className="py-2 font-medium" />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="py-2 pr-3">
                      <div className="font-medium text-slate-800">{r.full_name || [r.first_name, r.last_name].filter(Boolean).join(' ') || '(no name)'}</div>
                      <div className="text-[11px] text-slate-400">
                        {r.is_minor && <span className="mr-1.5 px-1 rounded bg-violet-50 text-violet-600">minor</span>}
                        {r.is_deceased && <span className="mr-1.5 px-1 rounded bg-slate-100 text-slate-500">deceased</span>}
                        {r.date_of_birth ? `DOB ${String(r.date_of_birth).slice(0, 10)}` : ''}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-slate-600 font-mono text-xs">{r.member_id || '—'}</td>
                    <td className="py-2 pr-3">
                      <div className="text-xs text-slate-600">
                        {[r.address_line1, r.city, r.state_code, r.postal_code].filter(Boolean).join(', ') || r.email || '—'}
                      </div>
                      {!r.contact_sufficient && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">insufficient contact</span>}
                    </td>
                    <td className="py-2 pr-3 text-xs text-slate-500 max-w-[200px] truncate" title={r.data_elements || ''}>{r.data_elements || '—'}</td>
                    <td className="py-2 pr-3">
                      {canEdit ? (
                        <select value={r.notification_status} onChange={(e) => patchRow(r.id, { notification_status: e.target.value })}
                          className={`text-xs border border-slate-200 rounded-lg px-1.5 py-1 ${STATUS_STYLES[r.notification_status] || ''}`}>
                          {STATUS_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      ) : (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[r.notification_status] || 'bg-slate-100 text-slate-600'}`}>
                          {(STATUS_OPTIONS.find(([k]) => k === r.notification_status) || [null, r.notification_status])[1]}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-[11px] text-slate-500 whitespace-nowrap">
                      {r.mailed_date && <div>mailed {String(r.mailed_date).slice(0, 10)}</div>}
                      {r.returned_date && <div className="text-red-600">returned {String(r.returned_date).slice(0, 10)}</div>}
                      {r.substitute_date && <div className="text-amber-600">substitute {String(r.substitute_date).slice(0, 10)}</div>}
                    </td>
                    {canEdit && (
                      <td className="py-2 text-right">
                        <button onClick={() => del(r.id)} className="text-slate-300 hover:text-red-500 text-xs">✕</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{total} individuals, page {page + 1} of {pages}</span>
              <div className="flex gap-2">
                <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className={btn + ' border border-slate-200 disabled:opacity-40'}>Previous</button>
                <button disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} className={btn + ' border border-slate-200 disabled:opacity-40'}>Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
